// 💾 src/pages/public/Market.tsx
import { useEffect, useState, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import "./style/Market.scss"

type BinanceTicker = {
  symbol: string
  lastPrice: string
  priceChangePercent: string
  highPrice: string
  lowPrice: string
  volume: string
}

type CoinInfo = {
  id: string       // e.g. "bitcoin"
  symbol: string   // e.g. "btc"
  name: string     // e.g. "Bitcoin"
  image: string    // URL to coin icon
}

type Tab = "spot" | "futures" | "favorites"

const Market: React.FC = () => {
  const { lng = "en" } = useParams<{ lng: string }>()
  const navigate = useNavigate()

  const [binanceData, setBinanceData] = useState<BinanceTicker[]>([])
  const [coins, setCoins] = useState<CoinInfo[]>([])
  const [search, setSearch] = useState("")
  const [tab, setTab] = useState<Tab>("spot")
  const [favorites, setFavorites] = useState<string[]>(() =>
    JSON.parse(localStorage.getItem("favorites") || "[]")
  )
  const [currentPage, setCurrentPage] = useState(1)
  const perPage = 25

  // 1) Fetch CoinGecko metadata
  useEffect(() => {
    const fetchCoins = async () => {
      try {
        const url =
          "https://api.coingecko.com/api/v3/coins/markets" +
          "?vs_currency=usd" +
          "&order=market_cap_desc" +
          "&per_page=200" +
          "&page=1" +
          "&sparkline=false"
        const res = await fetch(url)
        if (!res.ok) throw new Error(res.statusText)
        const data: CoinInfo[] = await res.json()
        setCoins(data)
      } catch (err) {
        console.error("CoinGecko fetch error:", err)
      }
    }
    fetchCoins()
  }, [])

  // 2) Fetch Binance 24h ticker data
  useEffect(() => {
    const fetchBinance = async () => {
      try {
        const res = await fetch("https://api.binance.com/api/v3/ticker/24hr")
        if (!res.ok) throw new Error(res.statusText)
        const data: BinanceTicker[] = await res.json()
        setBinanceData(data)
      } catch (err) {
        console.error("Binance fetch error:", err)
      }
    }
    fetchBinance()
    const interval = setInterval(fetchBinance, 5000)
    return () => clearInterval(interval)
  }, [])

  // 3) Filter & sort based on tab, search, favorites
  const filteredMarkets = useMemo(() => {
    const term = search.toLowerCase()
    const isFav = (sym: string) => favorites.includes(sym)

    return binanceData
      .filter((m) => {
        const sym = m.symbol
        const base = sym.replace("USDT", "")
        const isUsdtPair =
          sym.endsWith("USDT") && !sym.includes("DOWN") && !sym.includes("UP")
        const meta = coins.find((c) => c.symbol.toUpperCase() === base)
        if (!meta) return false

        const matchesSearch =
          base.toLowerCase().includes(term) ||
          meta.name.toLowerCase().includes(term)

        if (tab === "favorites") return isFav(sym) && matchesSearch
        if (tab === "spot") return isUsdtPair && matchesSearch
        if (tab === "futures") return !isUsdtPair && matchesSearch
        return false
      })
      .sort((a, b) => {
        const rank = (sym: string) =>
          coins.findIndex((c) => c.symbol.toUpperCase() === sym.replace("USDT", ""))
        return rank(a.symbol) - rank(b.symbol)
      })
  }, [binanceData, coins, favorites, search, tab])

  // 4) Paginate
  const paginatedMarkets = filteredMarkets.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  )

  // 5) Handlers
  const toggleFavorite = (symbol: string) => {
    const updated = favorites.includes(symbol)
      ? favorites.filter((s) => s !== symbol)
      : [...favorites, symbol]
    setFavorites(updated)
    localStorage.setItem("favorites", JSON.stringify(updated))
  }

  const goToTrade = (symbol: string) => {
    const formatted = symbol.replace("USDT", "_USDT")
    navigate(`/${lng}/exchange/${formatted}`)
  }

  return (
    <div className="market-page">
      <h1>Markets</h1>

      <div className="market-tabs">
        {(["spot", "futures", "favorites"] as Tab[]).map((t) => (
          <button
            key={t}
            className={tab === t ? "active" : ""}
            onClick={() => {
              setTab(t)
              setCurrentPage(1)
            }}
          >
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
        <input
          type="text"
          placeholder="Search BTC or Bitcoin..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <table className="market-table">
        <thead>
          <tr>
            <th>Fav</th>
            <th>Pair</th>
            <th>Price</th>
            <th>Change</th>
            <th>High / Low</th>
            <th>Vol(24h)</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {paginatedMarkets.map((m) => {
            const base = m.symbol.replace("USDT", "")
            const meta = coins.find((c) => c.symbol.toUpperCase() === base)!
            const isUp = parseFloat(m.priceChangePercent) >= 0
            const isFav = favorites.includes(m.symbol)
            return (
              <tr key={m.symbol}>
                <td>
                  <span
                    className="fav-icon"
                    onClick={() => toggleFavorite(m.symbol)}
                  >
                    {isFav ? "★" : "☆"}
                  </span>
                </td>
                <td>
                  <div className="pair-cell">
                    <img
                      src={meta.image}
                      alt={meta.name}
                      className="coin-icon"
                    />
                    <div>
                      <div className="pair">{base}/USDT</div>
                      <div className="name">{meta.name}</div>
                    </div>
                  </div>
                </td>
                <td>${parseFloat(m.lastPrice).toFixed(4)}</td>
                <td className={isUp ? "up" : "down"}>
                  {isUp ? "▲" : "▼"}{" "}
                  {parseFloat(m.priceChangePercent).toFixed(2)}%
                </td>
                <td>
                  {parseFloat(m.highPrice).toFixed(4)} /{" "}
                  {parseFloat(m.lowPrice).toFixed(4)}
                </td>
                <td>{parseFloat(m.volume).toLocaleString()}</td>
                <td>
                  <button onClick={() => goToTrade(m.symbol)}>Trade</button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="market-pagination">
        {Array.from(
          { length: Math.ceil(filteredMarkets.length / perPage) },
          (_, i) => i + 1
        ).map((page) => (
          <button
            key={page}
            className={currentPage === page ? "active" : ""}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </button>
        ))}
      </div>
    </div>
  )
}

export default Market
